import client_config from '@/assets/json/client_config.json'
import client_lookups from '@/assets/json/clientLookups.json'
import { useAuthStore } from '@/stores/auth'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { useLookupsStore } from '@/stores/lookups'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useEstablishmentDraftOverrideStore = defineStore('estDraftOverrideStore', () => {
  const payloadMapping = computed(() => {
    return {
      // 162: [
      //   'nocRequestNumber',
      //   'immigrationDepartmentId',
      //   'establishmentNumber',
      //   'cardIssueDate',
      //   'cardExpiryDate',
      //   'englishEstablishmentName',
      //   'arabicEstablishmentName',
      //   'economicActivityId',
      //   'immigrationDepartmentCode',
      //   'economicActivityCode',
      //   'sponsorDepartmentId'
      // ],
      393: [
        'insurancePolicyNumber',
        'insurancePolicySource',
        'insurancePolicyTypeId',
        'shortVisaQoutaNumber',
        'insuranceIssueDate',
        'insuranceExpiryDate'
      ]
    }
  })

  const knownDatesToFormat = computed(() => {
    return ['cardIssueDate', 'cardExpiryDate']
  })

  const formatDates = (payload) => {
    Object.entries(payload).forEach(([key, value]) => {
      if (knownDatesToFormat.value.includes(key)) {
        payload[key] = DateTime.fromFormat(value, 'dd/MM/yyyy').toFormat("yyyy-MM-dd'T'HH:mm:ss")
      }
    })
    return payload
  }

  const overridePayload = async (serviceId, { includeEstInfo = null } = {}) => {
    const formStore = useFormStore()
    const authStore = useAuthStore()

    const departments = await useLookupsStore().fetchLookup('/api/lookup/departments')
    const { immigrationDepartmentId } = authStore.establishmentInfo

    const mapping = payloadMapping.value[serviceId]

    const payload = {}

    /**
     * remove any and all extraneous fields from the payload.
     * this makes sure that the payload will only contain fields
     * that are defined in the schema/mapping
     */
    if (mapping) {
      Object.entries(formStore.form.values)
        .filter(([key, _]) => mapping.includes(key))
        .forEach(([key, value]) => (payload[key] = value))
    }

    Object.assign(payload, {
      ...formatDates(payload)
    })

    if (includeEstInfo) {
      const authStore = useAuthStore()
      const {
        englishName: estEnglishName,
        arabicName: estArabicName,
        departmentID: estDepartmentID,
        sponsorType: estSponsorType,
        number: estNumber
      } = authStore.establishmentInfo

      switch (includeEstInfo) {
        case 'sponsor':
          Object.assign(payload, {
            sponsorDepartmentId: estDepartmentID,
            sponsorNumber: estNumber,
            sponsorTypeId: estSponsorType,
            sponsorEnglishName: estEnglishName,
            sponsorArabicName: estArabicName
          })
          return

        default:
          return
      }
    }

    return {
      request: payload,
      serviceTransactionId: serviceId,
      administrativeRegionId: departments.find((d) => d.id == immigrationDepartmentId)
        ?.administrativeRegionId
    }
  }

  const overridePayload162 = async () => {
    const formStore = useFormStore()
    const authStore = useAuthStore()

    const departments = await useLookupsStore().fetchLookup('/api/lookup/departments')

    const {
      number,
      cardIssueDate,
      cardExpireDate: cardExpiryDate,
      englishName,
      arabicName,
      departmentID,
      departmentCode,
      departmentDescription
    } = authStore.establishmentInfo

    const {
      request: {
        addViolationRequest,
        EstablishmentCardFinsAmount,
        economicActivityId,
        economicActivityCode
      }
    } = formStore.draft

    const payload = {
      request: {
        establishmentRequestRelatedPersons: [],
        establishmentEconomicActivities: [],
        addViolationRequest,
        nocRequestNumber: null,
        establishmentNumber: number,
        cardIssueDate,
        cardExpiryDate,
        englishEstablishmentName: englishName,
        arabicEstablishmentName: arabicName,
        sponsorDepartmentId: departmentCode,
        economicActivityId,
        economicActivityCode,
        EstablishmentCardFinsAmount,
        immigrationDepartmentCode: departmentCode,
        immigrationDepartmentId: departmentDescription
      },
      administrativeRegionId: departments.find((d) => d.id == departmentID)?.administrativeRegionId
    }

    return payload
  }

  function getDeliveryDetails() {
    const formStore = useFormStore()
    const {
      deliveyCompany: { Tawzea, EmiratePost }
    } = client_lookups
    const {
      deliveryCompanyId,
      // tawzea
      tawzeaDeliveryAreaId,
      tawzeaDeliveryEmirateId,
      tawzeaDeliveryCityId,
      // emirates post
      emiratePostDeliveryAreaId,
      emiratePostDeliveryEmirateId,
      emiratePostDeliveryCityId
    } = formStore.form.values

    switch (deliveryCompanyId) {
      case Tawzea:
        return {
          deliveryEmirateId: tawzeaDeliveryEmirateId,
          deliveryCityId: tawzeaDeliveryCityId,
          deliveryAreaId: tawzeaDeliveryAreaId
        }
      case EmiratePost:
        return {
          deliveryEmirateId: emiratePostDeliveryEmirateId,
          deliveryCityId: emiratePostDeliveryCityId,
          deliveryAreaId: emiratePostDeliveryAreaId
        }

      default:
        return {
          deliveryEmirateId: null,
          deliveryCityId: null,
          deliveryAreaId: null
        }
    }
  }

  async function overrideIcaForm() {
    const formStore = useFormStore()
    const globalStore = useGlobalStore()
    const authStore = useAuthStore()

    const { hideOldEmiratesIdCardCopyOptionsForUnifiedForm } = client_config
    const {
      applicatClassType: { expat },
      permitCategoryType: { visa },
      passportRelation: { holder }
    } = client_lookups

    const { numberOfYears, preferedLanguageId } = formStore.form.values

    const {
      request: { changePersonPhotoFlag },
      requestNumber
    } = formStore.draft

    const preferredLanguage = {
      preferedLanguageId: 1,
      preferedLanguageCode: 'AR'
    }

    if (preferedLanguageId == 'EN') {
      Object.assign(preferredLanguage, {
        preferedLanguageId: 2,
        preferedLanguageCode: 'EN'
      })
    }

    const payload = {
      ...formStore.draft,
      request: {
        accompanies: [],
        addNewAddressFlag: false,
        companyName: '',
        ...formStore.draft.request,
        ...formStore.form.values,
        ...getDeliveryDetails(),
        applicantClassId: expat,
        permitTypeId: visa,
        userHadTheOldCardOrCopy: hideOldEmiratesIdCardCopyOptionsForUnifiedForm ? 0 : null,
        mainServiceTransactionId: globalStore.startedService.mainServiceTransactionId,
        manageFields: {},
        newIdCardDurationId: numberOfYears,
        passportRelationId: holder,
        changePersonPhotoFlag: parseInt(changePersonPhotoFlag) || 0,
        governmentalDepartmentId: '',
        govEstabDepartmentCode: null,
        deliveryLocalPobox: '',
        ...preferredLanguage
      }
    }

    if (requestNumber) {
      if (!authStore.establishmentInfo) {
        await authStore.fetchEstablishmentInfo()
      }

      const { arabicName, number, nationalityID } = authStore.establishmentInfo

      Object.assign(payload.request, {
        sponsorName: arabicName,
        sponsorNumber: number,
        sponsorNationalityId: nationalityID
      })
    }

    return payload
  }

  const actions = { overridePayload, overridePayload162, overrideIcaForm }

  return {
    ...actions
  }
})
